import React from 'react';
import { motion } from 'framer-motion';
import { Facebook, Twitter, Instagram, Linkedin, Youtube, Mail, Phone, Heart } from 'lucide-react';
import { useSiteData } from '../contexts/SiteDataContext';

const Footer = () => {
  const { author } = useSiteData();
  const authorData = author || {};
  const socialLinks = [
    { icon: Facebook, href: authorData.facebook, label: 'Facebook' },
    { icon: Twitter, href: authorData.twitter, label: 'Twitter' },
    { icon: Instagram, href: authorData.instagram, label: 'Instagram' },
    { icon: Linkedin, href: authorData.linkedin, label: 'LinkedIn' },
    { icon: Youtube, href: authorData.youtube, label: 'YouTube' },
  ];

  return (
    <footer className="bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-400 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-400 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 py-16 relative z-10">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          {/* About Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold mb-4 text-orange-400" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>{authorData.name}</h3>
            <p className="text-gray-300 leading-relaxed mb-4" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
              {authorData.bio}
            </p>
            <div className="flex gap-4">
              {socialLinks.filter((s) => s.href).map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.2, rotate: 5 }}
                  className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-orange-500 transition-all duration-300"
                >
                  <social.icon className="h-5 w-5" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl font-bold mb-4 text-orange-400" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>त्वरित लिंक</h3>
            <ul className="space-y-2">
              {[
                { label: 'होम', href: '#home' },
                { label: 'परिचय', href: '#about' },
                { label: 'पुस्तक', href: '#book-section' },
                { label: 'समीक्षाएं', href: '#reviews' },
                { label: 'गैलरी', href: '#gallery' },
                { label: 'ब्लॉग', href: '#blog' },
                { label: 'संपर्क', href: '#contact' },
              ].map((item, index) => (
                <li key={index}>
                  <a 
                    href={item.href}
                    style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
                    className="text-gray-300 hover:text-orange-400 transition-colors duration-300 hover:translate-x-2 inline-block"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl font-bold mb-4 text-orange-400" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>संपर्क</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-gray-300">
                <Mail className="h-5 w-5 text-orange-400" />
                <a href={`mailto:${authorData.email}`} className="hover:text-orange-400 transition-colors">
                  {authorData.email}
                </a>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <Phone className="h-5 w-5 text-orange-400" />
                <a href={`tel:${authorData.phone}`} className="hover:text-orange-400 transition-colors">
                  {authorData.phone}
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Manifesto Text */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="border-t border-white/20 pt-8 mb-8"
        >
          <p 
            className="text-center text-xl font-bold text-orange-400"
            style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
          >
            सेवा • संस्कार • समरसता • संगठन • पंचपरिवर्तन
          </p>
        </motion.div>

        {/* Copyright */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center text-gray-400 text-sm"
        >
          <p className="flex flex-wrap items-center justify-center gap-2" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
            © {new Date().getFullYear()} {authorData.name}. सर्वाधिकार सुरक्षित।
            <span className="flex items-center gap-1">
              भारत के लिए <Heart className="h-4 w-4 text-red-500 fill-current" /> से निर्मित
            </span>
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;